// MCHD Room Scheduler - Coordinator Dashboard Hook
// Created: 07-10-2025 14:01:32
// Purpose: React Query hook for coordinator dashboard data

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  getCoordinatorKPIs,
  getRoomStates,
  getUpcomingBlocks,
  getConflicts,
  getUtilizationData,
  getAssignmentCompleteness,
  type CoordinatorDashboardKPIs,
} from '../../api/endpoints/coordinator';

/**
 * Query parameters for coordinator dashboard
 */
export interface CoordinatorDashboardParams {
  startDate?: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
}

/**
 * Use Coordinator Dashboard KPIs
 * 
 * React Query hook to fetch coordinator dashboard KPIs.
 * Data is cached for 5 minutes per @tech-stack-documentation.mdc React Query best practices.
 * 
 * @param params - Query parameters for filtering
 * @returns UseQueryResult with KPIs data
 * 
 * @example
 * ```tsx
 * const { data: kpis, isLoading, error } = useCoordinatorKPIs({
 *   startDate: '2025-10-01',
 *   endDate: '2025-10-31',
 *   buildingId: 'building-123'
 * });
 * ```
 * 
 * Created: 07-10-2025 14:01:32
 * Complies: @react-frontend.mdc, @tech-stack-documentation.mdc
 */
export function useCoordinatorKPIs(
  params?: CoordinatorDashboardParams
): UseQueryResult<CoordinatorDashboardKPIs, Error> {
  return useQuery({
    queryKey: ['coordinator-kpis', params],
    queryFn: async () => {
      const response = await getCoordinatorKPIs(params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch KPIs');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Use Room States
 * 
 * React Query hook to fetch today's room states.
 * 
 * @param params - Query parameters
 * @returns UseQueryResult with room states
 * 
 * Created: 07-10-2025 14:01:32
 */
export function useRoomStates(
  params?: {
    date?: string;
    buildingId?: string;
    floorId?: string;
  }
): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ['room-states', params],
    queryFn: async () => {
      const response = await getRoomStates(params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch room states');
      }
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes (real-time data)
  });
}

/**
 * Use Upcoming Blocks
 * 
 * React Query hook to fetch upcoming maintenance blocks.
 * 
 * @param params - Query parameters
 * @returns UseQueryResult with upcoming blocks
 * 
 * Created: 07-10-2025 14:01:32
 */
export function useUpcomingBlocks(
  params?: CoordinatorDashboardParams
): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ['upcoming-blocks', params],
    queryFn: async () => {
      const response = await getUpcomingBlocks(params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch upcoming blocks');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Use Conflicts
 * 
 * React Query hook to fetch conflicts.
 * 
 * @param params - Query parameters
 * @returns UseQueryResult with conflicts data
 * 
 * Created: 07-10-2025 14:01:32
 */
export function useConflicts(
  params?: CoordinatorDashboardParams & { roomId?: string }
): UseQueryResult<any[], Error> {
  return useQuery({
    queryKey: ['conflicts', params],
    queryFn: async () => {
      const response = await getConflicts(params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch conflicts');
      }
      return response.data;
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
  });
}

/**
 * Use Utilization Data
 * 
 * React Query hook to fetch utilization data by room.
 * 
 * @param params - Query parameters
 * @returns UseQueryResult with utilization data
 * 
 * Created: 07-10-2025 14:01:32
 */
export function useUtilizationData(
  params?: CoordinatorDashboardParams & { groupBy?: string }
): UseQueryResult<{ byRoom: any[]; byTime: any[] }, Error> {
  return useQuery({
    queryKey: ['utilization-data', params],
    queryFn: async () => {
      const response = await getUtilizationData(params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch utilization data');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Use Assignment Completeness
 * 
 * React Query hook to fetch assignment completeness metrics.
 * 
 * @param params - Query parameters
 * @returns UseQueryResult with completeness data
 * 
 * Created: 07-10-2025 14:01:32
 */
export function useAssignmentCompleteness(
  params?: CoordinatorDashboardParams
): UseQueryResult<{
  total: number;
  completed: number;
  pending: number;
  conflicts: number;
  completeness: number;
}, Error> {
  return useQuery({
    queryKey: ['assignment-completeness', params],
    queryFn: async () => {
      const response = await getAssignmentCompleteness(params);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to fetch assignment completeness');
      }
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}
